<template>
  <div class="sales-dashboard min-h-screen">
    <LoadingSpinner :isLoading="isLoading" />
    
    <!-- Header Section -->
    <div class="sd-header">
      <div class="w-full px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="sd-header__logo">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
              </svg>
            </div>
            <div>
              <h1 class="text-xl font-semibold text-secondary-800">{{ $t('products.title') }}</h1>
              <p class="text-sm text-gray-500">{{ $t('products.manage_description') }}</p>
            </div>
          </div>
          <div class="flex items-center space-x-3">
            <button class="sd-btn sd-btn--secondary" @click="showExportModal">
              <BaseIcon icon="file-pdf" class="mr-2" />
              {{ $t('products.export_pricelist') }}
            </button>
            <button v-if="isAdmin" class="sd-btn sd-btn--secondary" @click="showSubcategoryManager">
              <BaseIcon icon="folder" class="mr-2" />
              {{ $t('products.manage_subcategories') }}
            </button>
            <button v-if="isAdmin" class="sd-btn sd-btn--primary" @click="showAddModal">
              <BaseIcon icon="plus" class="mr-2" />
              {{ $t('products.add_product') }}
            </button>
          </div>
        </div>
      </div>
    </div>
      
    <!-- Search and Grouping -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div class="flex flex-wrap items-center gap-4">
        <!-- Search Field -->
        <div class="relative flex-1 max-w-md">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input 
            v-model="searchQuery" 
            type="text" 
            :placeholder="$t('products.search_placeholder')" 
            class="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-securdia-blue/20 focus:border-securdia-blue transition-colors"
          />
          <button 
            v-if="searchQuery" 
            @click="searchQuery = ''" 
            class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="flex items-center">
          <button
            @click="viewMode = 'products'"
            class="px-4 py-2 text-sm font-medium border"
            :class="viewMode === 'products' ? 'bg-gray-100 text-gray-900 border-gray-300' : 'bg-white text-gray-600 hover:bg-gray-50 border-gray-200'"
          >
            {{ $t('sales_dashboard.products') }}
          </button>
          <button
            @click="viewMode = 'suppliers'"
            class="px-4 py-2 text-sm font-medium border -ml-px"
            :class="viewMode === 'suppliers' ? 'bg-gray-100 text-gray-900 border-gray-300' : 'bg-white text-gray-600 hover:bg-gray-50 border-gray-200'"
          >
            {{ $t('sales_dashboard.cat_supplier') }}
          </button>
        </div>
        
        <div class="h-6 w-px bg-gray-300"></div>
        <!-- Group by -->
        <div class="flex items-center space-x-2">
          <span class="text-sm font-medium text-gray-600 mr-2">{{ $t('products.group_by') }}:</span>
          <button 
            @click="groupBy = 'category'" 
            class="px-4 py-2 text-sm font-medium border"
            :class="groupBy === 'category' ? 'bg-gray-100 text-gray-900 border-gray-300' : 'bg-white text-gray-600 hover:bg-gray-50 border-gray-200'"
          >
            {{ $t('products.category') }}
          </button>
          <button 
            @click="groupBy = 'manufacturer'" 
            class="px-4 py-2 text-sm font-medium border"
            :class="groupBy === 'manufacturer' ? 'bg-gray-100 text-gray-900 border-gray-300' : 'bg-white text-gray-600 hover:bg-gray-50 border-gray-200'"
          >
            {{ $t('products.manufacturer') }}
          </button>
        </div>
      </div>

      <!-- Products Grid -->
      <div class="space-y-4 mt-6">
      <div v-for="group in groupOptions" :key="group.value" class="product-group">
        <div 
          v-if="groupedProducts[group.value] && groupedProducts[group.value].length > 0"
          class="bg-white border border-gray-200 mb-3"
        >
          <!-- Group Header -->
          <div 
            class="px-6 py-3 bg-gray-50 cursor-pointer flex justify-between items-center hover:bg-gray-100 border-b border-gray-200"
            @click="toggleGroup(group.value)"
          >
            <div class="flex items-center">
              <BaseIcon icon="box" class="text-gray-600 mr-3" />
              <div>
                <h2 class="text-base font-semibold text-gray-800">{{ group.label }}</h2>
                <p class="text-xs text-gray-500">{{ groupedProducts[group.value].length }} {{ $t('products.items') }}</p>
              </div>
            </div>
            <BaseIcon 
              :icon="expandedGroups[group.value] ? 'chevron-up' : 'chevron-down'" 
              class="text-gray-500"
            />
          </div>
          
          <!-- Expanded Content -->
          <transition name="slide-fade">
            <div v-show="expandedGroups[group.value]" class="bg-white">
              
              <!-- Subcategories Section (only for category grouping) -->
              <div v-if="groupBy === 'category' && subcategoriesByParent[group.value] && subcategoriesByParent[group.value].length > 0">
                
                <!-- Subcategory Rows -->
                <div v-for="subcategory in subcategoriesByParent[group.value]" :key="subcategory._id" class="border-b border-gray-100">
                  <!-- Subcategory Header -->
                  <div 
                    class="px-6 py-2 pl-10 bg-blue-50 cursor-pointer flex justify-between items-center hover:bg-blue-100"
                    @click="toggleSubcategory(subcategory._id)"
                  >
                    <div class="flex items-center">
                      <BaseIcon icon="folder-open" class="text-blue-500 mr-2 text-sm" />
                      <span class="text-sm font-medium text-blue-800">{{ subcategory.displayName }}</span>
                      <span class="ml-2 text-xs text-blue-600">({{ getProductsForSubcategory(group.value, subcategory._id).length }})</span>
                    </div>
                    <BaseIcon 
                      :icon="expandedSubcategories[subcategory._id] ? 'chevron-up' : 'chevron-down'" 
                      class="text-blue-500 text-sm"
                    />
                  </div>
                  
                  <!-- Subcategory Products Table -->
                  <transition name="slide-fade">
                    <div v-show="expandedSubcategories[subcategory._id]" class="pl-8 overflow-x-auto">
                      <table class="w-full table-fixed" v-if="getProductsForSubcategory(group.value, subcategory._id).length > 0">
                        <thead class="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th scope="col" class="w-1/4 px-6 py-2 text-left text-xs font-semibold text-gray-700 uppercase">{{ $t('products.product_name') }}</th>
                            <th scope="col" class="w-28 px-6 py-2 text-left text-xs font-semibold text-gray-700 uppercase">{{ $t('products.article_number') }}</th>
                            <th scope="col" class="w-24 px-6 py-2 text-left text-xs font-semibold text-gray-700 uppercase">{{ $t('products.manufacturer') }}</th>
                            <th scope="col" class="w-32 px-6 py-2 text-center text-xs font-semibold text-gray-700 uppercase">{{ purchasePriceLabel(group.value) }}</th>
                            <th scope="col" class="w-32 px-6 py-2 text-center text-xs font-semibold text-gray-700 uppercase" v-if="group.value !== 'software' && group.value !== 'simcard_supplier' && group.value !== 'alarm_central_supplier'">{{ $t('products.recommended_cash_price') }}</th>
                            <th scope="col" class="w-32 px-6 py-2 text-center text-xs font-semibold text-gray-700 uppercase" v-if="group.value !== 'simcard_supplier' && group.value !== 'alarm_central_supplier'">{{ $t('products.recommended_rental_price') }}</th>
                            <th scope="col" class="w-36 px-6 py-2 text-center text-xs font-semibold text-gray-700 uppercase" v-if="isAdmin">{{ $t('products.actions') }}</th>
                          </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                          <tr 
                            v-for="row in getFlattenedRowsForSubcategory(group.value, subcategory._id)"
                            :key="row.key"
                            :class="row.isVariant ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'"
                          >
                            <template v-if="row.isVariant">
                              <td class="px-6 py-2 pl-10 break-words">
                                <div class="flex items-center min-w-0">
                                  <span class="text-blue-600 mr-2">↳</span>
                                  <span class="text-sm text-gray-700 break-words">{{ row.variant.name }}</span>
                                </div>
                              </td>
                              <td class="px-6 py-2 whitespace-nowrap text-sm text-gray-600">
                                <span v-if="row.variant.articleNumber" class="font-mono text-xs bg-white px-2 py-1">{{ row.variant.articleNumber }}</span>
                                <span v-else class="text-gray-400">-</span>
                              </td>
                              <td class="px-6 py-2"></td>
                              <td class="px-6 py-2 whitespace-nowrap text-center text-sm text-gray-600">{{ row.variant.purchase_price !== null ? row.variant.purchase_price : '-' }}</td>
                              <td class="px-6 py-2 whitespace-nowrap text-center text-sm text-gray-600" v-if="row.product.category !== 'software' && row.product.category !== 'simcard_supplier' && row.product.category !== 'alarm_central_supplier'">{{ row.variant.recommended_cash_price !== null ? row.variant.recommended_cash_price : '-' }}</td>
                              <td class="px-6 py-2 whitespace-nowrap text-center text-sm text-gray-600" v-if="row.product.category !== 'simcard_supplier' && row.product.category !== 'alarm_central_supplier'">{{ row.variant.recommended_rental_price !== null ? row.variant.recommended_rental_price : '-' }}</td>
                              <td class="px-6 py-2" v-if="isAdmin"></td>
                            </template>
                            <template v-else>
                              <td class="px-6 py-3 max-w-xs break-words">
                                <div class="flex items-center">
                                  <button v-if="row.product.hasVariants && row.product.variants && row.product.variants.length > 0" @click.stop="toggleVariants(row.product._id)" class="mr-2 text-gray-500 hover:text-gray-700 flex-shrink-0">
                                    <BaseIcon :icon="expandedVariants[row.product._id] ? 'chevron-down' : 'chevron-right'" />
                                  </button>
                                  <div v-else class="w-6 mr-2 flex-shrink-0"></div>
                                  <div class="min-w-0">
                                    <div v-if="row.product.display_name" class="text-sm font-bold text-gray-800 break-words">{{ row.product.display_name }}</div>
                                    <div class="text-xs text-gray-600 break-words" :class="row.product.display_name ? 'ml-0' : 'font-medium text-gray-900'">{{ row.product.name }}</div>
                                    <div v-if="row.product.hasVariants && row.product.variants" class="text-xs text-blue-600 mt-1">{{ $t('products.variant_count', { count: row.product.variants.length }) }}</div>
                                  </div>
                                </div>
                              </td>
                              <td class="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                                <span v-if="row.product.articleNumber" class="font-mono text-xs bg-gray-100 px-2 py-1">{{ row.product.articleNumber }}</span>
                                <span v-else class="text-gray-400">-</span>
                              </td>
                              <td class="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                                <span class="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700">{{ getManufacturerLabel(row.product.manufacturer) }}</span>
                              </td>
                              <td class="px-6 py-3 whitespace-nowrap text-center">
                                <template v-if="row.product.hasVariants && row.product.variants && row.product.variants.length > 0">
                                  <span class="text-gray-400">-</span>
                                </template>
                                <template v-else>
                                  <input v-model.number="row.product.purchase_price" @change="updateProduct(row.product)" type="number" min="0" step="0.01" :disabled="!isAdmin" class="w-24 px-2 py-1 text-sm text-center border border-gray-300 focus:outline-none focus:border-gray-500" :class="{ 'bg-gray-100 cursor-not-allowed text-gray-500': !isAdmin }" />
                                </template>
                              </td>
                              <td class="px-6 py-3 whitespace-nowrap text-center" v-if="row.product.category !== 'software' && row.product.category !== 'simcard_supplier' && row.product.category !== 'alarm_central_supplier'">
                                <template v-if="row.product.hasVariants && row.product.variants && row.product.variants.length > 0">
                                  <span class="text-gray-400">-</span>
                                </template>
                                <template v-else>
                                  <input v-model.number="row.product.recommended_cash_price" @change="updateProduct(row.product)" type="number" min="0" step="0.01" :disabled="!isAdmin" class="w-24 px-2 py-1 text-sm text-center border border-gray-300 focus:outline-none focus:border-gray-500" :class="{ 'bg-gray-100 cursor-not-allowed text-gray-500': !isAdmin }" />
                                </template>
                              </td>
                              <td class="px-6 py-3 whitespace-nowrap text-center" v-if="row.product.category !== 'simcard_supplier' && row.product.category !== 'alarm_central_supplier'">
                                <template v-if="row.product.hasVariants && row.product.variants && row.product.variants.length > 0">
                                  <span class="text-gray-400">-</span>
                                </template>
                                <template v-else>
                                  <input v-model.number="row.product.recommended_rental_price" @change="updateProduct(row.product)" type="number" min="0" step="0.01" :disabled="!isAdmin" class="w-24 px-2 py-1 text-sm text-center border border-gray-300 focus:outline-none focus:border-gray-500" :class="{ 'bg-gray-100 cursor-not-allowed text-gray-500': !isAdmin }" />
                                </template>
                              </td>
                              <td class="px-6 py-3 whitespace-nowrap text-center" v-if="isAdmin">
                                <button class="btn-secondary-outline mr-1" @click="showVariantModal(row.product)" title="Hantera varianter"><BaseIcon icon="layer-group" /></button>
                                <button class="btn-secondary-outline mr-1" @click="showEditModal(row.product)"><BaseIcon icon="pencil-alt" /></button>
                                <button class="btn-red-outline" @click="deleteProduct(row.product)"><BaseIcon icon="trash" /></button>
                              </td>
                            </template>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </transition>
                </div>
                
                <!-- Products Without Subcategory -->
                <div v-if="getProductsWithoutSubcategory(group.value).length > 0" class="border-b border-gray-100">
                  <div 
                    class="px-6 py-2 pl-10 bg-gray-100 cursor-pointer flex justify-between items-center hover:bg-gray-200"
                    @click="toggleSubcategory(`${group.value}-none`)"
                  >
                    <div class="flex items-center">
                      <BaseIcon icon="folder" class="text-gray-500 mr-2 text-sm" />
                      <span class="text-sm font-medium text-gray-600">{{ $t('products.without_subcategory') }}</span>
                      <span class="ml-2 text-xs text-gray-500">({{ getProductsWithoutSubcategory(group.value).length }})</span>
                    </div>
                    <BaseIcon 
                      :icon="expandedSubcategories[`${group.value}-none`] ? 'chevron-up' : 'chevron-down'" 
                      class="text-gray-500 text-sm"
                    />
                  </div>
                  
                  <transition name="slide-fade">
                    <div v-show="expandedSubcategories[`${group.value}-none`]" class="pl-8 overflow-x-auto">
                      <table class="w-full table-fixed">
                        <thead class="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th scope="col" class="w-1/4 px-6 py-2 text-left text-xs font-semibold text-gray-700 uppercase">{{ $t('products.product_name') }}</th>
                            <th scope="col" class="w-28 px-6 py-2 text-left text-xs font-semibold text-gray-700 uppercase">{{ $t('products.article_number') }}</th>
                            <th scope="col" class="w-24 px-6 py-2 text-left text-xs font-semibold text-gray-700 uppercase">{{ $t('products.manufacturer') }}</th>
                            <th scope="col" class="w-32 px-6 py-2 text-center text-xs font-semibold text-gray-700 uppercase">{{ purchasePriceLabel(group.value) }}</th>
                            <th scope="col" class="w-32 px-6 py-2 text-center text-xs font-semibold text-gray-700 uppercase" v-if="group.value !== 'software' && group.value !== 'simcard_supplier' && group.value !== 'alarm_central_supplier'">{{ $t('products.recommended_cash_price') }}</th>
                            <th scope="col" class="w-32 px-6 py-2 text-center text-xs font-semibold text-gray-700 uppercase" v-if="group.value !== 'simcard_supplier' && group.value !== 'alarm_central_supplier'">{{ $t('products.recommended_rental_price') }}</th>
                            <th scope="col" class="w-36 px-6 py-2 text-center text-xs font-semibold text-gray-700 uppercase" v-if="isAdmin">{{ $t('products.actions') }}</th>
                          </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                          <tr 
                            v-for="row in getFlattenedRowsWithoutSubcategory(group.value)"
                            :key="row.key"
                            :class="row.isVariant ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'"
                          >
                            <template v-if="row.isVariant">
                              <td class="px-6 py-2 pl-10 break-words">
                                <div class="flex items-center min-w-0">
                                  <span class="text-blue-600 mr-2">↳</span>
                                  <span class="text-sm text-gray-700 break-words">{{ row.variant.name }}</span>
                                </div>
                              </td>
                              <td class="px-6 py-2 whitespace-nowrap text-sm text-gray-600">
                                <span v-if="row.variant.articleNumber" class="font-mono text-xs bg-white px-2 py-1">{{ row.variant.articleNumber }}</span>
                                <span v-else class="text-gray-400">-</span>
                              </td>
                              <td class="px-6 py-2"></td>
                              <td class="px-6 py-2 whitespace-nowrap text-center text-sm text-gray-600">{{ row.variant.purchase_price !== null ? row.variant.purchase_price : '-' }}</td>
                              <td class="px-6 py-2 whitespace-nowrap text-center text-sm text-gray-600" v-if="row.product.category !== 'software' && row.product.category !== 'simcard_supplier' && row.product.category !== 'alarm_central_supplier'">{{ row.variant.recommended_cash_price !== null ? row.variant.recommended_cash_price : '-' }}</td>
                              <td class="px-6 py-2 whitespace-nowrap text-center text-sm text-gray-600" v-if="row.product.category !== 'simcard_supplier' && row.product.category !== 'alarm_central_supplier'">{{ row.variant.recommended_rental_price !== null ? row.variant.recommended_rental_price : '-' }}</td>
                              <td class="px-6 py-2" v-if="isAdmin"></td>
                            </template>
                            <template v-else>
                              <td class="px-6 py-3 max-w-xs break-words">
                                <div class="flex items-center">
                                  <button v-if="row.product.hasVariants && row.product.variants && row.product.variants.length > 0" @click.stop="toggleVariants(row.product._id)" class="mr-2 text-gray-500 hover:text-gray-700 flex-shrink-0">
                                    <BaseIcon :icon="expandedVariants[row.product._id] ? 'chevron-down' : 'chevron-right'" />
                                  </button>
                                  <div v-else class="w-6 mr-2 flex-shrink-0"></div>
                                  <div class="min-w-0">
                                    <div v-if="row.product.display_name" class="text-sm font-bold text-gray-800 break-words">{{ row.product.display_name }}</div>
                                    <div class="text-xs text-gray-600 break-words" :class="row.product.display_name ? 'ml-0' : 'font-medium text-gray-900'">{{ row.product.name }}</div>
                                    <div v-if="row.product.hasVariants && row.product.variants" class="text-xs text-blue-600 mt-1">{{ $t('products.variant_count', { count: row.product.variants.length }) }}</div>
                                  </div>
                                </div>
                              </td>
                              <td class="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                                <span v-if="row.product.articleNumber" class="font-mono text-xs bg-gray-100 px-2 py-1">{{ row.product.articleNumber }}</span>
                                <span v-else class="text-gray-400">-</span>
                              </td>
                              <td class="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                                <span class="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700">{{ getManufacturerLabel(row.product.manufacturer) }}</span>
                              </td>
                              <td class="px-6 py-3 whitespace-nowrap text-center">
                                <template v-if="row.product.hasVariants && row.product.variants && row.product.variants.length > 0">
                                  <span class="text-gray-400">-</span>
                                </template>
                                <template v-else>
                                  <input v-model.number="row.product.purchase_price" @change="updateProduct(row.product)" type="number" min="0" step="0.01" :disabled="!isAdmin" class="w-24 px-2 py-1 text-sm text-center border border-gray-300 focus:outline-none focus:border-gray-500" :class="{ 'bg-gray-100 cursor-not-allowed text-gray-500': !isAdmin }" />
                                </template>
                              </td>
                              <td class="px-6 py-3 whitespace-nowrap text-center" v-if="row.product.category !== 'software' && row.product.category !== 'simcard_supplier' && row.product.category !== 'alarm_central_supplier'">
                                <template v-if="row.product.hasVariants && row.product.variants && row.product.variants.length > 0">
                                  <span class="text-gray-400">-</span>
                                </template>
                                <template v-else>
                                  <input v-model.number="row.product.recommended_cash_price" @change="updateProduct(row.product)" type="number" min="0" step="0.01" :disabled="!isAdmin" class="w-24 px-2 py-1 text-sm text-center border border-gray-300 focus:outline-none focus:border-gray-500" :class="{ 'bg-gray-100 cursor-not-allowed text-gray-500': !isAdmin }" />
                                </template>
                              </td>
                              <td class="px-6 py-3 whitespace-nowrap text-center" v-if="row.product.category !== 'simcard_supplier' && row.product.category !== 'alarm_central_supplier'">
                                <template v-if="row.product.hasVariants && row.product.variants && row.product.variants.length > 0">
                                  <span class="text-gray-400">-</span>
                                </template>
                                <template v-else>
                                  <input v-model.number="row.product.recommended_rental_price" @change="updateProduct(row.product)" type="number" min="0" step="0.01" :disabled="!isAdmin" class="w-24 px-2 py-1 text-sm text-center border border-gray-300 focus:outline-none focus:border-gray-500" :class="{ 'bg-gray-100 cursor-not-allowed text-gray-500': !isAdmin }" />
                                </template>
                              </td>
                              <td class="px-6 py-3 whitespace-nowrap text-center" v-if="isAdmin">
                                <button class="btn-secondary-outline mr-1" @click="showVariantModal(row.product)" title="Hantera varianter"><BaseIcon icon="layer-group" /></button>
                                <button class="btn-secondary-outline mr-1" @click="showEditModal(row.product)"><BaseIcon icon="pencil-alt" /></button>
                                <button class="btn-red-outline" @click="deleteProduct(row.product)"><BaseIcon icon="trash" /></button>
                              </td>
                            </template>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </transition>
                </div>
              </div>
              
              <!-- Standard Products Table (for manufacturer grouping or categories without subcategories) -->
              <div v-else class="overflow-x-auto">
                <table class="w-full table-fixed">
                  <thead class="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th scope="col" class="w-1/4 px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{{ $t('products.product_name') }}</th>
                      <th scope="col" class="w-28 px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">{{ $t('products.article_number') }}</th>
                      <th scope="col" class="w-24 px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" v-if="groupBy === 'manufacturer'">{{ $t('products.category') }}</th>
                      <th scope="col" class="w-24 px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase" v-if="groupBy === 'category'">{{ $t('products.manufacturer') }}</th>
                      <th scope="col" class="w-32 px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">{{ purchasePriceLabel(group.value) }}</th>
                      <th scope="col" class="w-32 px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase" v-if="group.value !== 'software' && group.value !== 'simcard_supplier' && group.value !== 'alarm_central_supplier'">{{ $t('products.recommended_cash_price') }}</th>
                      <th scope="col" class="w-32 px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase" v-if="group.value !== 'simcard_supplier' && group.value !== 'alarm_central_supplier'">{{ $t('products.recommended_rental_price') }}</th>
                      <th scope="col" class="w-36 px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase" v-if="isAdmin">{{ $t('products.actions') }}</th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr 
                      v-for="row in getFlattenedRows(group.value)" 
                      :key="row.key"
                      :class="row.isVariant ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'"
                    >
                      <template v-if="row.isVariant">
                        <td class="px-6 py-3 pl-14 break-words">
                          <div class="flex items-center min-w-0">
                            <span class="text-blue-600 mr-2">↳</span>
                            <span class="text-sm text-gray-700 break-words">{{ row.variant.name }}</span>
                          </div>
                        </td>
                        <td class="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                          <span v-if="row.variant.articleNumber" class="font-mono text-xs bg-white px-2 py-1">{{ row.variant.articleNumber }}</span>
                          <span v-else class="text-gray-400">-</span>
                        </td>
                        <td class="px-6 py-3" v-if="groupBy === 'manufacturer'"></td>
                        <td class="px-6 py-3" v-if="groupBy === 'category'"></td>
                        <td class="px-6 py-3 whitespace-nowrap text-center text-sm text-gray-600">{{ row.variant.purchase_price !== null ? row.variant.purchase_price : '-' }}</td>
                        <td class="px-6 py-3 whitespace-nowrap text-center text-sm text-gray-600" v-if="row.product.category !== 'software' && row.product.category !== 'simcard_supplier' && row.product.category !== 'alarm_central_supplier'">{{ row.variant.recommended_cash_price !== null ? row.variant.recommended_cash_price : '-' }}</td>
                        <td class="px-6 py-3 whitespace-nowrap text-center text-sm text-gray-600" v-if="row.product.category !== 'simcard_supplier' && row.product.category !== 'alarm_central_supplier'">{{ row.variant.recommended_rental_price !== null ? row.variant.recommended_rental_price : '-' }}</td>
                        <td class="px-6 py-3" v-if="isAdmin"></td>
                      </template>
                      <template v-else>
                        <td class="px-6 py-4 max-w-xs break-words">
                          <div class="flex items-center">
                            <button v-if="row.product.hasVariants && row.product.variants && row.product.variants.length > 0" @click.stop="toggleVariants(row.product._id)" class="mr-2 text-gray-500 hover:text-gray-700 flex-shrink-0">
                              <BaseIcon :icon="expandedVariants[row.product._id] ? 'chevron-down' : 'chevron-right'" />
                            </button>
                            <div v-else class="w-6 mr-2 flex-shrink-0"></div>
                            <div class="min-w-0">
                              <div v-if="row.product.display_name" class="text-sm font-bold text-gray-800 break-words">{{ row.product.display_name }}</div>
                              <div class="text-xs text-gray-600 break-words" :class="row.product.display_name ? 'ml-0' : 'font-medium text-gray-900'">{{ row.product.name }}</div>
                              <div v-if="row.product.hasVariants && row.product.variants" class="text-xs text-blue-600 mt-1">{{ $t('products.variant_count', { count: row.product.variants.length }) }}</div>
                            </div>
                          </div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          <span v-if="row.product.articleNumber" class="font-mono text-xs bg-gray-100 px-2 py-1">{{ row.product.articleNumber }}</span>
                          <span v-else class="text-gray-400">-</span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600" v-if="groupBy === 'manufacturer'">
                          <span class="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700">{{ getCategoryLabel(row.product.category) }}</span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600" v-if="groupBy === 'category'">
                          <span class="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700">{{ getManufacturerLabel(row.product.manufacturer) }}</span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-center">
                          <template v-if="row.product.hasVariants && row.product.variants && row.product.variants.length > 0">
                            <span class="text-gray-400">-</span>
                          </template>
                          <template v-else>
                            <input v-model.number="row.product.purchase_price" @change="updateProduct(row.product)" type="number" min="0" step="0.01" :disabled="!isAdmin" class="w-24 px-3 py-1 text-sm text-center border border-gray-300 focus:outline-none focus:border-gray-500" :class="{ 'bg-gray-100 cursor-not-allowed text-gray-500': !isAdmin }" />
                          </template>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-center" v-if="row.product.category !== 'software' && row.product.category !== 'simcard_supplier' && row.product.category !== 'alarm_central_supplier'">
                          <template v-if="row.product.hasVariants && row.product.variants && row.product.variants.length > 0">
                            <span class="text-gray-400">-</span>
                          </template>
                          <template v-else>
                            <input v-model.number="row.product.recommended_cash_price" @change="updateProduct(row.product)" type="number" min="0" step="0.01" :disabled="!isAdmin" class="w-24 px-3 py-1 text-sm text-center border border-gray-300 focus:outline-none focus:border-gray-500" :class="{ 'bg-gray-100 cursor-not-allowed text-gray-500': !isAdmin }" />
                          </template>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-center" v-if="row.product.category !== 'simcard_supplier' && row.product.category !== 'alarm_central_supplier'">
                          <template v-if="row.product.hasVariants && row.product.variants && row.product.variants.length > 0">
                            <span class="text-gray-400">-</span>
                          </template>
                          <template v-else>
                            <input v-model.number="row.product.recommended_rental_price" @change="updateProduct(row.product)" type="number" min="0" step="0.01" :disabled="!isAdmin" class="w-24 px-3 py-1 text-sm text-center border border-gray-300 focus:outline-none focus:border-gray-500" :class="{ 'bg-gray-100 cursor-not-allowed text-gray-500': !isAdmin }" />
                          </template>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-center" v-if="isAdmin">
                          <button class="btn-secondary-outline mr-1" @click="showVariantModal(row.product)" title="Hantera varianter"><BaseIcon icon="layer-group" /></button>
                          <button class="btn-secondary-outline mr-1" @click="showEditModal(row.product)"><BaseIcon icon="pencil-alt" /></button>
                          <button class="btn-red-outline" @click="deleteProduct(row.product)"><BaseIcon icon="trash" /></button>
                        </td>
                      </template>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </transition>
        </div>
      </div>
      
      <!-- Empty State -->
      <div v-if="Object.keys(groupedProducts).length === 0" class="bg-white border border-gray-200 p-12 text-center">
        <BaseIcon icon="box-open" class="text-5xl text-gray-300 mb-4" />
        <p class="text-gray-600">{{ $t('products.no_products') }}</p>
        <button v-if="isAdmin" class="mt-4 btn-primary-outline" @click="showAddModal">
          {{ $t('products.add_first_product') }}
        </button>
      </div>
    </div>
    </div>

    <!-- Add Product Modal -->
    <modal name="add-product" :width="650" height="auto" :scrollable="true" :adaptive="true">
      <div class="p-6 bg-white">
        <div class="flex items-center justify-between mb-6 pb-3 border-b border-gray-200">
          <h2 class="text-xl font-semibold text-gray-800">{{ $t('products.add_product') }}</h2>
          <button @click="hideAddModal" class="text-gray-400 hover:text-gray-600">
            <BaseIcon icon="times" />
          </button>
        </div>
        <ValidationObserver ref="formAddProduct">
          <div class="space-y-5">
            <div class="grid grid-cols-2 gap-4">
              <BaseInput v-model="newProduct.display_name" placeholder="Centralenhet" :field_name="$t('products.display_name')" />
              <BaseInput v-model="newProduct.articleNumber" placeholder="3612288" :field_name="$t('products.article_number')" />
            </div>
            <BaseInput v-model="newProduct.name" placeholder="AJAX Hub 2 Plus" :field_name="$t('products.product_name')" rules="required" />
            <div class="grid grid-cols-2 gap-4">
              <BaseSelect 
                v-model="newProduct.category" 
                :options="categoryOptions" 
                nameSelector="label" 
                optionSelector="value"
                :field_name="$t('products.category')" 
                rules="required" 
                @input="onCategoryChange('new')"
              />
              <BaseSelect 
                v-model="newProduct.subcategory" 
                :options="newProductSubcategoryOptions" 
                nameSelector="label" 
                optionSelector="value"
                :field_name="$t('products.subcategory')" 
              />
            </div>
            <BaseSelect 
              v-model="newProduct.manufacturer" 
              :options="manufacturerOptions" 
              nameSelector="label" 
              optionSelector="value"
              :field_name="$t('products.manufacturer')" 
              rules="required" 
            />
            <div class="grid grid-cols-3 gap-4">
              <BaseInput v-model.number="newProduct.purchase_price" type="number" min="0" step="0.01" :field_name="purchasePriceLabel(newProduct.category)" />
              <BaseInput v-if="newProduct.category !== 'software' && newProduct.category !== 'simcard_supplier' && newProduct.category !== 'alarm_central_supplier'" v-model.number="newProduct.recommended_cash_price" type="number" min="0" step="0.01" :field_name="$t('products.recommended_cash_price')" />
              <BaseInput v-if="newProduct.category !== 'simcard_supplier' && newProduct.category !== 'alarm_central_supplier'" v-model.number="newProduct.recommended_rental_price" type="number" min="0" step="0.01" :field_name="$t('products.recommended_rental_price')" />
            </div>
          </div>
          <div class="mt-8 flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button @click="hideAddModal" class="btn-secondary-outline px-6 py-2">
              {{ $t('products.cancel') }}
            </button>
            <button @click="createProduct" class="btn-primary px-6 py-2">
              {{ $t('products.save') }}
            </button>
          </div>
        </ValidationObserver>
      </div>
    </modal>

    <!-- Edit Product Modal -->
    <modal name="edit-product" :width="650" height="auto" :scrollable="true" :adaptive="true">
      <div class="p-6 bg-white">
        <div class="flex items-center justify-between mb-6 pb-3 border-b border-gray-200">
          <h2 class="text-xl font-semibold text-gray-800">{{ $t('products.edit_product') }}</h2>
          <button @click="hideEditModal" class="text-gray-400 hover:text-gray-600">
            <BaseIcon icon="times" />
          </button>
        </div>
        <ValidationObserver ref="formEditProduct">
          <div class="space-y-5">
            <div class="grid grid-cols-2 gap-4">
              <BaseInput v-model="editProduct.display_name" :field_name="$t('products.display_name')" />
              <BaseInput v-model="editProduct.articleNumber" :field_name="$t('products.article_number')" />
            </div>
            <BaseInput v-model="editProduct.name" :field_name="$t('products.product_name')" rules="required" />
            <div class="grid grid-cols-2 gap-4">
              <BaseSelect 
                v-model="editProduct.category" 
                :options="categoryOptions" 
                nameSelector="label" 
                optionSelector="value"
                :field_name="$t('products.category')" 
                rules="required" 
                @input="onCategoryChange('edit')"
              />
              <BaseSelect 
                v-model="editProduct.subcategory" 
                :options="editProductSubcategoryOptions" 
                nameSelector="label" 
                optionSelector="value"
                :field_name="$t('products.subcategory')" 
              />
            </div>
            <BaseSelect 
              v-model="editProduct.manufacturer" 
              :options="manufacturerOptions" 
              nameSelector="label" 
              optionSelector="value"
              :field_name="$t('products.manufacturer')" 
              rules="required" 
            />
            <div class="grid grid-cols-3 gap-4">
              <BaseInput v-model.number="editProduct.purchase_price" type="number" min="0" step="0.01" :field_name="purchasePriceLabel(editProduct.category)" />
              <BaseInput v-if="editProduct.category !== 'software' && editProduct.category !== 'simcard_supplier' && editProduct.category !== 'alarm_central_supplier'" v-model.number="editProduct.recommended_cash_price" type="number" min="0" step="0.01" :field_name="$t('products.recommended_cash_price')" />
              <BaseInput v-if="editProduct.category !== 'simcard_supplier' && editProduct.category !== 'alarm_central_supplier'" v-model.number="editProduct.recommended_rental_price" type="number" min="0" step="0.01" :field_name="$t('products.recommended_rental_price')" />
            </div>
          </div>
          <div class="mt-8 flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button @click="hideEditModal" class="btn-secondary-outline px-6 py-2">
              {{ $t('products.cancel') }}
            </button>
            <button @click="saveEditProduct" class="btn-primary px-6 py-2">
              {{ $t('products.save') }}
            </button>
          </div>
        </ValidationObserver>
      </div>
    </modal>

    <!-- Variant Management Modal -->
    <modal name="variant-modal" :width="700" height="auto" :scrollable="true" :adaptive="true">
      <div class="p-6 bg-white">
        <div class="flex items-center justify-between mb-6 pb-3 border-b border-gray-200">
          <div>
            <h2 class="text-xl font-semibold text-gray-800">{{ $t('products.variants') }}</h2>
            <p class="text-sm text-gray-500 mt-1">{{ variantProduct ? variantProduct.name : '' }}</p>
          </div>
          <button @click="hideVariantModal" class="text-gray-400 hover:text-gray-600">
            <BaseIcon icon="times" />
          </button>
        </div>
        
        <!-- Existing Variants List -->
        <div v-if="variantProduct && variantProduct.variants && variantProduct.variants.length > 0" class="mb-6">
          <h3 class="text-sm font-semibold text-gray-700 mb-3">{{ $t('products.variants') }} ({{ variantProduct.variants.length }})</h3>
          <div class="space-y-2">
            <div 
              v-for="variant in variantProduct.variants" 
              :key="variant._id"
              class="flex items-center justify-between p-3 bg-gray-50 border border-gray-200"
            >
              <div class="flex-1">
                <div class="font-medium text-gray-800">{{ variant.name }}</div>
                <div class="text-xs text-gray-500 mt-1">
                  <span v-if="variant.articleNumber" class="font-mono bg-gray-200 px-1">{{ variant.articleNumber }}</span>
                  <span v-if="variant.purchase_price !== null" class="ml-2">{{ $t('products.purchase_label') }}: {{ variant.purchase_price }} {{ $t('products.currency_suffix') }}</span>
                </div>
              </div>
              <div class="flex items-center space-x-2">
                <button @click="editVariant(variant)" class="btn-secondary-outline btn-sm">
                  <BaseIcon icon="pencil-alt" />
                </button>
                <button @click="deleteVariant(variant)" class="btn-red-outline btn-sm">
                  <BaseIcon icon="trash" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="mb-6 p-4 bg-gray-50 text-center text-gray-500">
          {{ $t('products.no_variants') }}
        </div>
        
        <!-- Add/Edit Variant Form -->
        <div class="border-t border-gray-200 pt-4">
          <h3 class="text-sm font-semibold text-gray-700 mb-3">
            {{ editingVariant ? $t('products.edit_variant') : $t('products.add_variant') }}
          </h3>
          <div class="space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <BaseInput v-model="newVariant.name" :field_name="$t('products.variant_name')" placeholder="RB (Heat/smoke/CO)" />
              <BaseInput v-model="newVariant.articleNumber" :field_name="$t('products.article_number')" placeholder="3609196" />
            </div>
            <div class="grid grid-cols-3 gap-4">
              <BaseInput v-model.number="newVariant.purchase_price" type="number" min="0" step="0.01" :field_name="purchasePriceLabel(variantProduct && variantProduct.category)" :placeholder="variantProduct && variantProduct.purchase_price ? String(variantProduct.purchase_price) : ''" />
              <BaseInput v-if="!variantProduct || (variantProduct.category !== 'software' && variantProduct.category !== 'simcard_supplier' && variantProduct.category !== 'alarm_central_supplier')" v-model.number="newVariant.recommended_cash_price" type="number" min="0" step="0.01" :field_name="$t('products.recommended_cash_price')" :placeholder="variantProduct && variantProduct.recommended_cash_price ? String(variantProduct.recommended_cash_price) : ''" />
              <BaseInput v-if="!variantProduct || (variantProduct.category !== 'simcard_supplier' && variantProduct.category !== 'alarm_central_supplier')" v-model.number="newVariant.recommended_rental_price" type="number" min="0" step="0.01" :field_name="$t('products.recommended_rental_price')" :placeholder="variantProduct && variantProduct.recommended_rental_price ? String(variantProduct.recommended_rental_price) : ''" />
            </div>
            <div class="flex justify-end space-x-3">
              <button v-if="editingVariant" @click="cancelEditVariant" class="btn-secondary-outline px-4 py-2">
                {{ $t('products.cancel') }}
              </button>
              <button @click="saveVariant" class="btn-primary px-4 py-2">
                {{ editingVariant ? $t('products.save') : $t('products.add_variant') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </modal>

    <!-- Subcategory Management Modal -->
    <modal name="subcategory-modal" :width="800" height="auto" :scrollable="true" :adaptive="true">
      <div class="p-6 bg-white">
        <div class="flex items-center justify-between mb-6 pb-3 border-b border-gray-200">
          <h2 class="text-xl font-semibold text-gray-800">{{ $t('products.manage_subcategories') }}</h2>
          <button @click="hideSubcategoryManager" class="text-gray-400 hover:text-gray-600">
            <BaseIcon icon="times" />
          </button>
        </div>
        
        <!-- Subcategories grouped by parent category -->
        <div class="space-y-4 mb-6 max-h-96 overflow-y-auto">
          <div v-for="cat in categoryOptions" :key="cat.value" class="border border-gray-200">
            <div class="px-4 py-2 bg-gray-50 font-semibold text-gray-700">{{ cat.label }}</div>
            <div class="p-3">
              <div v-if="subcategoriesByParent[cat.value] && subcategoriesByParent[cat.value].length > 0" class="space-y-2">
                <div 
                  v-for="sub in subcategoriesByParent[cat.value]" 
                  :key="sub._id"
                  class="flex items-center justify-between p-2 bg-white border border-gray-100 hover:bg-gray-50"
                >
                  <div>
                    <span class="font-medium">{{ sub.displayName }}</span>
                    <span class="text-xs text-gray-500 ml-2">({{ sub.name }})</span>
                  </div>
                  <div class="flex items-center space-x-2">
                    <button @click="editSubcategory(sub)" class="btn-secondary-outline btn-sm">
                      <BaseIcon icon="pencil-alt" />
                    </button>
                    <button @click="deleteSubcategory(sub)" class="btn-red-outline btn-sm">
                      <BaseIcon icon="trash" />
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="text-gray-400 text-sm py-2">{{ $t('products.no_subcategories') }}</div>
            </div>
          </div>
        </div>
        
        <!-- Add/Edit Subcategory Form -->
        <div class="border-t border-gray-200 pt-4">
          <h3 class="text-sm font-semibold text-gray-700 mb-3">
            {{ editingSubcategory ? $t('products.edit_subcategory') : $t('products.add_subcategory') }}
          </h3>
          <div class="space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <BaseInput v-model="newSubcategory.name" :field_name="$t('products.subcategory_name')" placeholder="grund" />
              <BaseInput v-model="newSubcategory.displayName" :field_name="$t('products.subcategory_display_name')" placeholder="GRUND" />
            </div>
            <div class="grid grid-cols-2 gap-4">
              <BaseSelect 
                v-model="newSubcategory.parentCategory" 
                :options="categoryOptions" 
                nameSelector="label" 
                optionSelector="value"
                :field_name="$t('products.parent_category')" 
              />
              <BaseInput v-model.number="newSubcategory.sortOrder" type="number" min="0" :field_name="$t('products.sort_order')" />
            </div>
            <div class="flex justify-end space-x-3">
              <button v-if="editingSubcategory" @click="cancelEditSubcategory" class="btn-secondary-outline px-4 py-2">
                {{ $t('products.cancel') }}
              </button>
              <button @click="saveSubcategory" class="btn-primary px-4 py-2">
                {{ editingSubcategory ? $t('products.save') : $t('products.add_subcategory') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </modal>

    <!-- Export Price List Modal -->
    <modal name="export-modal" :width="800" height="auto" :scrollable="true" :adaptive="true">
      <div class="p-6 bg-white">
        <div class="flex items-center justify-between mb-6 pb-3 border-b border-gray-200">
          <div>
            <h2 class="text-xl font-semibold text-gray-800">{{ $t('products.export_pricelist') }}</h2>
            <p class="text-sm text-gray-500 mt-1">{{ $t('products.export_description') }}</p>
          </div>
          <button @click="hideExportModal" class="text-gray-400 hover:text-gray-600">
            <BaseIcon icon="times" />
          </button>
        </div>
        
        <div class="grid grid-cols-2 gap-6">
          <!-- Left Column: Selection -->
          <div class="space-y-4">
            <!-- Group by selection -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">{{ $t('products.export_group_by') }}</label>
              <div class="flex space-x-2">
                <button 
                  @click="exportOptions.groupBy = 'category'" 
                  class="px-4 py-2 text-sm font-medium border rounded-lg"
                  :class="exportOptions.groupBy === 'category' ? 'bg-securdia-blue text-white border-securdia-blue' : 'bg-white text-gray-600 hover:bg-gray-50 border-gray-300'"
                >
                  {{ $t('products.category') }}
                </button>
                <button 
                  @click="exportOptions.groupBy = 'manufacturer'" 
                  class="px-4 py-2 text-sm font-medium border rounded-lg"
                  :class="exportOptions.groupBy === 'manufacturer' ? 'bg-securdia-blue text-white border-securdia-blue' : 'bg-white text-gray-600 hover:bg-gray-50 border-gray-300'"
                >
                  {{ $t('products.manufacturer') }}
                </button>
              </div>
            </div>

            <!-- Categories/Manufacturers Selection -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                {{ exportOptions.groupBy === 'category' ? $t('products.select_categories') : $t('products.select_manufacturers') }}
              </label>
              <div class="border border-gray-300 rounded-lg p-3 max-h-64 overflow-y-auto bg-gray-50">
                <label class="flex items-center mb-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    :checked="allGroupsSelected"
                    @change="toggleAllGroups"
                    class="w-4 h-4 text-securdia-blue border-gray-300 rounded focus:ring-securdia-blue"
                  />
                  <span class="ml-2 text-sm font-medium text-gray-700">{{ $t('products.select_all') }}</span>
                </label>
                <div class="border-t border-gray-200 pt-2 mt-2">
                  <div 
                    v-for="option in currentGroupOptions" 
                    :key="option.value" 
                    class="mb-2"
                  >
                    <!-- Category/Manufacturer checkbox -->
                    <label class="flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        :checked="isCategoryFullySelected(option.value)"
                        :indeterminate.prop="isCategoryPartiallySelected(option.value)"
                        @change="toggleCategorySelection(option.value)"
                        class="w-4 h-4 text-securdia-blue border-gray-300 rounded focus:ring-securdia-blue"
                      />
                      <span class="ml-2 text-sm text-gray-700 font-medium">{{ option.label }}</span>
                      <span class="ml-auto text-xs text-gray-500">({{ getProductCountForGroup(option.value) }})</span>
                    </label>
                    
                    <!-- Subcategories (only for category grouping) -->
                    <div v-if="exportOptions.groupBy === 'category' && subcategoriesByParent[option.value] && subcategoriesByParent[option.value].length > 0" class="ml-6 mt-1 space-y-1">
                      <label 
                        v-for="sub in subcategoriesByParent[option.value]" 
                        :key="sub._id" 
                        class="flex items-center cursor-pointer"
                      >
                        <input 
                          type="checkbox" 
                          :value="sub._id" 
                          v-model="exportOptions.selectedSubcategories"
                          @change="onSubcategoryChange(option.value)"
                          class="w-3.5 h-3.5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span class="ml-2 text-xs text-gray-600">{{ sub.displayName }}</span>
                        <span class="ml-auto text-xs text-gray-400">({{ getProductCountForSubcategory(option.value, sub._id) }})</span>
                      </label>
                      <!-- Without subcategory option -->
                      <label class="flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          :value="`${option.value}-none`" 
                          v-model="exportOptions.selectedSubcategories"
                          @change="onSubcategoryChange(option.value)"
                          class="w-3.5 h-3.5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span class="ml-2 text-xs text-gray-500 italic">{{ $t('products.without_subcategory') }}</span>
                        <span class="ml-auto text-xs text-gray-400">({{ getProductCountWithoutSubcategory(option.value) }})</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column: Fields to include -->
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">{{ $t('products.select_fields') }}</label>
              <div class="border border-gray-300 rounded-lg p-3 bg-gray-50">
                <label class="flex items-center mb-3 cursor-pointer">
                  <input 
                    type="checkbox" 
                    v-model="exportOptions.fields.articleNumber"
                    class="w-4 h-4 text-securdia-blue border-gray-300 rounded focus:ring-securdia-blue"
                  />
                  <span class="ml-2 text-sm text-gray-700">{{ $t('products.article_number') }}</span>
                </label>
                <label class="flex items-center mb-3 cursor-pointer">
                  <input 
                    type="checkbox" 
                    v-model="exportOptions.fields.purchasePrice"
                    class="w-4 h-4 text-securdia-blue border-gray-300 rounded focus:ring-securdia-blue"
                  />
                  <span class="ml-2 text-sm text-gray-700">{{ $t('products.purchase_price') }}</span>
                </label>
                <label class="flex items-center mb-3 cursor-pointer">
                  <input 
                    type="checkbox" 
                    v-model="exportOptions.fields.recommendedCashPrice"
                    class="w-4 h-4 text-securdia-blue border-gray-300 rounded focus:ring-securdia-blue"
                  />
                  <span class="ml-2 text-sm text-gray-700">{{ $t('products.recommended_cash_price') }}</span>
                </label>
                <label class="flex items-center mb-3 cursor-pointer">
                  <input 
                    type="checkbox" 
                    v-model="exportOptions.fields.recommendedRentalPrice"
                    class="w-4 h-4 text-securdia-blue border-gray-300 rounded focus:ring-securdia-blue"
                  />
                  <span class="ml-2 text-sm text-gray-700">{{ $t('products.recommended_rental_price') }}</span>
                </label>
                <label class="flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    v-model="exportOptions.fields.includeVariants"
                    class="w-4 h-4 text-securdia-blue border-gray-300 rounded focus:ring-securdia-blue"
                  />
                  <span class="ml-2 text-sm text-gray-700">{{ $t('products.include_variants') }}</span>
                </label>
              </div>
            </div>

            <!-- PDF Title -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">{{ $t('products.pdf_title') }}</label>
              <input 
                v-model="exportOptions.pdfTitle" 
                type="text" 
                class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-securdia-blue/20 focus:border-securdia-blue"
                :placeholder="$t('products.pricelist_default_title')"
              />
            </div>

            <!-- Summary -->
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div class="text-sm text-blue-800">
                <BaseIcon icon="info-circle" class="mr-1" />
                {{ $t('products.export_summary', { count: exportProductCount }) }}
              </div>
            </div>
          </div>
        </div>
        
        <div class="mt-6 flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button @click="hideExportModal" class="btn-secondary-outline px-6 py-2">
            {{ $t('products.cancel') }}
          </button>
          <button 
            @click="generatePDF" 
            :disabled="exportProductCount === 0 || isGeneratingPDF"
            class="btn-primary px-6 py-2 flex items-center"
            :class="{ 'opacity-50 cursor-not-allowed': exportProductCount === 0 || isGeneratingPDF }"
          >
            <BaseIcon v-if="isGeneratingPDF" icon="spinner" class="mr-2 animate-spin" />
            <BaseIcon v-else icon="file-pdf" class="mr-2" />
            {{ isGeneratingPDF ? $t('products.generating') : $t('products.generate_pdf') }}
          </button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import jsPDF from 'jspdf';
import 'jspdf-autotable';
export default {
  title() {
    return `${this.$t('products.title')} | SecurCloud`;
  },
  data() {
    return {
      products: [],
      subcategories: [],
      isLoading: false,
      searchQuery: '',
      groupBy: 'category',
      viewMode: 'products',
      expandedGroups: {},
      expandedSubcategories: {},
      expandedVariants: {},
      newProduct: {
        display_name: '',
        name: '',
        type: '',
        articleNumber: '',
        category: '',
        subcategory: '',
        manufacturer: '',
        purchase_price: 0,
        recommended_rental_price: 0,
        recommended_cash_price: 0,
        is_software: false
      },
      editProduct: {
        _id: '',
        display_name: '',
        name: '',
        type: '',
        articleNumber: '',
        category: '',
        subcategory: '',
        manufacturer: '',
        purchase_price: 0,
        recommended_rental_price: 0,
        recommended_cash_price: 0,
        is_software: false
      },
      // Variant management
      variantProduct: null,
      newVariant: {
        name: '',
        articleNumber: '',
        purchase_price: null,
        recommended_cash_price: null,
        recommended_rental_price: null
      },
      editingVariant: null,
      // Subcategory management
      newSubcategory: {
        name: '',
        displayName: '',
        parentCategory: '',
        sortOrder: 0
      },
      editingSubcategory: null,
      // Export options
      exportOptions: {
        groupBy: 'category',
        selectedGroups: [],
        selectedSubcategories: [],
        fields: {
          articleNumber: true,
          purchasePrice: false,
          recommendedCashPrice: true,
          recommendedRentalPrice: true,
          includeVariants: true
        },
        pdfTitle: ''
      },
      isGeneratingPDF: false,
      manufacturerOptions: [
        { value: 'srt', label: 'SRT' },
        { value: 'twig', label: 'TWIG' },
        { value: 'emerit', label: 'EMERIT' },
        { value: 'ajax', label: 'AJAX' },
        { value: 'teltonika', label: 'TELTONIKA' },
        { value: 'anchor', label: 'Anchor' },
        { value: 'securdia', label: 'Securdia' },
        { value: 'other', label: 'Annat' }
      ]
    };
  },
  computed: {
    categoryOptions() {
      return [
        { value: 'alarm', label: this.$t('sales_dashboard.cat_personal_alarm') },
        { value: 'tag', label: this.$t('sales_dashboard.cat_tags') },
        { value: 'display', label: this.$t('sales_dashboard.cat_display') },
        { value: 'ajax', label: this.$t('sales_dashboard.cat_perimeter') },
        { value: 'vehicle_tracker', label: this.$t('sales_dashboard.cat_vehicle_tracker') },
        { value: 'software', label: this.$t('sales_dashboard.cat_software') },
        { value: 'simcard', label: this.$t('sales_dashboard.cat_simcard') },
        { value: 'accessory', label: this.$t('sales_dashboard.cat_accessory') },
        { value: 'other', label: this.$t('sales_dashboard.cat_other') },
        { value: 'simcard_supplier', label: this.$t('sales_dashboard.simcard_supplier') },
        { value: 'alarm_central_supplier', label: this.$t('sales_dashboard.alarm_central_supplier') },
      ];
    },
    isAdmin() {
      return this.$store.state.user && this.$store.state.user.role === 'admin';
    },
    groupOptions() {
      return this.groupBy === 'category' ? this.categoryOptions : this.manufacturerOptions;
    },
    // Filter products by search query and view mode
    filteredProducts() {
      let products = this.products;

      // Filter by view mode
      if (this.viewMode === 'products') {
        products = products.filter(p => p.category !== 'simcard_supplier' && p.category !== 'alarm_central_supplier');
      } else if (this.viewMode === 'suppliers') {
        products = products.filter(p => p.category === 'simcard_supplier' || p.category === 'alarm_central_supplier');
      }

      if (!this.searchQuery || !this.searchQuery.trim()) {
        return products;
      }
      const query = this.searchQuery.toLowerCase().trim();
      return products.filter(product => {
        const displayName = (product.display_name || '').toLowerCase();
        const name = (product.name || '').toLowerCase();
        const articleNumber = (product.articleNumber || '').toLowerCase();
        return displayName.includes(query) || name.includes(query) || articleNumber.includes(query);
      });
    },
    groupedProducts() {
      const grouped = {};
      const productsToGroup = this.filteredProducts;
      
      const groupField = this.groupBy === 'category' ? 'category' : 'manufacturer';
      productsToGroup.forEach(product => {
        const key = product[groupField];
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(product);
      });
      
      // Sort products within each group by type
      Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => a.type.localeCompare(b.type));
      });
      
      return grouped;
    },
    // Get subcategories for a specific category
    subcategoriesByParent() {
      const grouped = {};
      this.subcategories.forEach(sub => {
        if (!grouped[sub.parentCategory]) {
          grouped[sub.parentCategory] = [];
        }
        grouped[sub.parentCategory].push(sub);
      });
      // Sort subcategories by sortOrder
      Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      });
      return grouped;
    },
    newProductSubcategoryOptions() {
      const options = [{ value: '', label: this.$t('products.no_subcategory') }];
      if (this.newProduct.category) {
        const subs = this.subcategories.filter(s => s.parentCategory === this.newProduct.category);
        subs.forEach(s => options.push({ value: s._id, label: s.displayName }));
      }
      return options;
    },
    editProductSubcategoryOptions() {
      const options = [{ value: '', label: this.$t('products.no_subcategory') }];
      if (this.editProduct.category) {
        const subs = this.subcategories.filter(s => s.parentCategory === this.editProduct.category);
        subs.forEach(s => options.push({ value: s._id, label: s.displayName }));
      }
      return options;
    },
    currentGroupOptions() {
      return this.exportOptions.groupBy === 'category' ? this.categoryOptions : this.manufacturerOptions;
    },
    allGroupsSelected() {
      if (this.exportOptions.groupBy === 'manufacturer') {
        return this.currentGroupOptions.length > 0 && 
               this.exportOptions.selectedGroups.length === this.currentGroupOptions.length;
      }
      // For categories, check if all subcategories are selected
      return this.currentGroupOptions.every(cat => this.isCategoryFullySelected(cat.value));
    },
    exportProductCount() {
      if (this.exportOptions.groupBy === 'manufacturer') {
        if (this.exportOptions.selectedGroups.length === 0) return 0;
        return this.products.filter(p => this.exportOptions.selectedGroups.includes(p.manufacturer)).length;
      }
      // For categories, filter by selected subcategories
      return this.products.filter(p => this.isProductSelectedForExport(p)).length;
    }
  },
  mounted() {
    this.fetchProducts();
    this.fetchSubcategories();
    this.expandAllGroups();
  },
  watch: {
    groupBy() {
      this.expandAllGroups();
    }
  },
  methods: {
    async fetchProducts() {
      this.isLoading = true;
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/products`);
        this.products = response.data.data;
      } catch (error) {
        this.handleError(error, this.$t('products.fetch_error'));
      } finally {
        this.isLoading = false;
      }
    },
    async fetchSubcategories() {
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/subcategories`);
        this.subcategories = response.data.data;
      } catch (error) {
        console.error('Failed to fetch subcategories:', error);
      }
    },
    getFlattenedRows(groupValue) {
      const products = this.groupedProducts[groupValue] || [];
      const rows = [];
      
      for (const product of products) {
        // Add the main product row
        rows.push({
          key: `product-${product._id}`,
          isVariant: false,
          product: product,
          variant: null
        });
        
        // Add variant rows if expanded
        if (this.expandedVariants[product._id] && product.variants && product.variants.length > 0) {
          for (const variant of product.variants) {
            rows.push({
              key: `variant-${product._id}-${variant._id}`,
              isVariant: true,
              product: product,
              variant: variant
            });
          }
        }
      }
      
      return rows;
    },
    // Get products that belong to a specific subcategory within a category
    getProductsForSubcategory(categoryValue, subcategoryId) {
      const products = this.groupedProducts[categoryValue] || [];
      // Compare as strings since subcategory can be ObjectId or string
      return products.filter(p => {
        if (!p.subcategory) return false;
        const productSubcatId = typeof p.subcategory === 'object' ? p.subcategory._id : p.subcategory;
        return String(productSubcatId) === String(subcategoryId);
      });
    },
    // Get products without any subcategory within a category
    getProductsWithoutSubcategory(categoryValue) {
      const products = this.groupedProducts[categoryValue] || [];
      return products.filter(p => !p.subcategory);
    },
    // Flattened rows for products in a specific subcategory
    getFlattenedRowsForSubcategory(categoryValue, subcategoryId) {
      const products = this.getProductsForSubcategory(categoryValue, subcategoryId);
      return this.buildFlattenedRows(products);
    },
    // Flattened rows for products without subcategory
    getFlattenedRowsWithoutSubcategory(categoryValue) {
      const products = this.getProductsWithoutSubcategory(categoryValue);
      return this.buildFlattenedRows(products);
    },
    // Helper to build flattened rows from a product array
    buildFlattenedRows(products) {
      const rows = [];
      for (const product of products) {
        rows.push({
          key: `product-${product._id}`,
          isVariant: false,
          product: product,
          variant: null
        });
        if (this.expandedVariants[product._id] && product.variants && product.variants.length > 0) {
          for (const variant of product.variants) {
            rows.push({
              key: `variant-${product._id}-${variant._id}`,
              isVariant: true,
              product: product,
              variant: variant
            });
          }
        }
      }
      return rows;
    },
    // Toggle subcategory expansion
    toggleSubcategory(subcategoryId) {
      this.$set(this.expandedSubcategories, subcategoryId, !this.expandedSubcategories[subcategoryId]);
    },
    async createProduct() {
      const isValid = await this.$refs.formAddProduct.validate();
      if (!isValid) return;

      try {
        const productData = { ...this.newProduct };
        // Auto-generate type from product name: lowercase and replace spaces with underscores
        productData.type = productData.name.toLowerCase().replace(/\s+/g, '_');
        if (!productData.subcategory) {
          productData.subcategory = null;
        }
        // Auto-set is_software based on category
        productData.is_software = productData.category === 'software';
        await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/products`, productData);
        this.handleSuccess(this.$t('products.product_created'));
        this.hideAddModal();
        this.fetchProducts();
      } catch (error) {
        this.handleError(error, this.$t('products.create_error'));
      }
    },
    async updateProduct(product) {
      if (!this.isAdmin) return;
      
      try {
        await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/products/${product._id}`, {
          purchase_price: product.purchase_price,
          recommended_rental_price: product.recommended_rental_price,
          recommended_cash_price: product.recommended_cash_price
        });
        // Sync purchase_price to pending commissions
        this.syncPriceToCommissions(product._id, product.purchase_price);
        this.handleSuccess(this.$t('products.product_updated'));
      } catch (error) {
        this.handleError(error, this.$t('products.update_error'));
        this.fetchProducts();
      }
    },
    syncPriceToCommissions(productId, purchasePrice) {
      if (productId && purchasePrice != null) {
        this.axios.put(
          `${process.env.VUE_APP_SERVER_URL}/sales/commissions/sync-product-price/${productId}`,
          { purchase_price: purchasePrice }
        ).catch(err => console.error('Failed to sync price to commissions:', err));
      }
    },
    deleteProduct(product) {
      this.$modal.show('dialog', {
        title: `<span class="text-red-500">${this.$t('products.confirm_delete', { name: product.name })}</span>`,
        buttons: [
          {
            title: '<div class="bg-accent-500 text-white text-sm font-sans py-2">'+this.$t('products.cancel')+'</div>',
            handler: () => {
              this.$modal.hide('dialog');
            }
          },
          {
            title: '<div class="bg-red-500 text-white text-sm font-sans py-2">Ta bort</div>',
            handler: () => {
              this.confirmDelete(product._id);
              this.$modal.hide('dialog');
            }
          }
        ]
      });
    },
    async confirmDelete(productId) {
      try {
        await this.axios.delete(`${process.env.VUE_APP_SERVER_URL}/products/${productId}`);
        this.handleSuccess(this.$t('products.product_deleted'));
        this.fetchProducts();
      } catch (error) {
        this.handleError(error, this.$t('products.delete_error'));
      }
    },
    showAddModal() {
      this.newProduct = {
        display_name: '',
        name: '',
        type: '',
        articleNumber: '',
        category: '',
        subcategory: '',
        manufacturer: '',
        purchase_price: 0,
        recommended_rental_price: 0,
        recommended_cash_price: 0,
        is_software: false
      };
      this.$modal.show('add-product');
    },
    hideAddModal() {
      this.$modal.hide('add-product');
    },
    showEditModal(product) {
      // Extract subcategory ID if it's an object (populated from DB)
      let subcategoryValue = '';
      if (product.subcategory) {
        subcategoryValue = typeof product.subcategory === 'object' ? product.subcategory._id : product.subcategory;
      }
      
      this.editProduct = {
        _id: product._id,
        display_name: product.display_name || '',
        name: product.name,
        type: product.type,
        articleNumber: product.articleNumber || '',
        category: product.category,
        subcategory: subcategoryValue,
        manufacturer: product.manufacturer,
        purchase_price: product.purchase_price || 0,
        recommended_rental_price: product.recommended_rental_price || 0,
        recommended_cash_price: product.recommended_cash_price || 0,
        is_software: product.is_software || false
      };
      this.$modal.show('edit-product');
    },
    hideEditModal() {
      this.$modal.hide('edit-product');
    },
    async saveEditProduct() {
      const isValid = await this.$refs.formEditProduct.validate();
      if (!isValid) return;

      try {
        const productData = { ...this.editProduct };
        const productId = productData._id;
        delete productData._id;
        if (!productData.subcategory) {
          productData.subcategory = null;
        }
        // Auto-set is_software based on category
        productData.is_software = productData.category === 'software';
        await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/products/${productId}`, productData);
        // Sync purchase_price to pending commissions
        this.syncPriceToCommissions(productId, productData.purchase_price);
        this.handleSuccess(this.$t('products.product_updated'));
        this.hideEditModal();
        this.fetchProducts();
      } catch (error) {
        this.handleError(error, this.$t('products.update_error'));
      }
    },
    onCategoryChange(mode) {
      // Reset subcategory when category changes
      if (mode === 'new') {
        this.newProduct.subcategory = '';
      } else {
        this.editProduct.subcategory = '';
      }
    },
    toggleGroup(group) {
      this.$set(this.expandedGroups, group, !this.expandedGroups[group]);
    },
    toggleVariants(productId) {
      this.$set(this.expandedVariants, productId, !this.expandedVariants[productId]);
    },
    expandAllGroups() {
      this.expandedGroups = {};
      this.groupOptions.forEach(g => {
        this.$set(this.expandedGroups, g.value, true);
      });
    },
    getCategoryLabel(category) {
      const cat = this.categoryOptions.find(c => c.value === category);
      return cat ? cat.label : category;
    },
    purchasePriceLabel(categoryValue) {
      return (categoryValue === 'software' || categoryValue === 'simcard_supplier' || categoryValue === 'alarm_central_supplier')
        ? 'Kostnad/mån'
        : this.$t('products.purchase_price');
    },
    getManufacturerLabel(manufacturer) {
      const man = this.manufacturerOptions.find(m => m.value === manufacturer);
      return man ? man.label : manufacturer;
    },
    
    // Variant methods
    showVariantModal(product) {
      this.variantProduct = product;
      this.resetVariantForm();
      this.$modal.show('variant-modal');
    },
    hideVariantModal() {
      this.$modal.hide('variant-modal');
      this.variantProduct = null;
      this.resetVariantForm();
    },
    resetVariantForm() {
      this.newVariant = {
        name: '',
        articleNumber: '',
        purchase_price: null,
        recommended_cash_price: null,
        recommended_rental_price: null
      };
      this.editingVariant = null;
    },
    editVariant(variant) {
      this.editingVariant = variant;
      this.newVariant = {
        name: variant.name,
        articleNumber: variant.articleNumber || '',
        purchase_price: variant.purchase_price,
        recommended_cash_price: variant.recommended_cash_price,
        recommended_rental_price: variant.recommended_rental_price
      };
    },
    cancelEditVariant() {
      this.resetVariantForm();
    },
    async saveVariant() {
      if (!this.newVariant.name) {
        this.handleError(null, 'Variant name is required');
        return;
      }
      
      try {
        if (this.editingVariant) {
          // Update existing variant
          await this.axios.put(
            `${process.env.VUE_APP_SERVER_URL}/products/${this.variantProduct._id}/variants/${this.editingVariant._id}`,
            this.newVariant
          );
          this.handleSuccess(this.$t('products.variant_updated'));
        } else {
          // Add new variant
          await this.axios.post(
            `${process.env.VUE_APP_SERVER_URL}/products/${this.variantProduct._id}/variants`,
            this.newVariant
          );
          this.handleSuccess(this.$t('products.variant_added'));
        }
        
        // Refresh product data
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/products/${this.variantProduct._id}`);
        this.variantProduct = response.data.data;
        this.fetchProducts();
        this.resetVariantForm();
      } catch (error) {
        this.handleError(error, 'Failed to save variant');
      }
    },
    async deleteVariant(variant) {
      try {
        await this.axios.delete(
          `${process.env.VUE_APP_SERVER_URL}/products/${this.variantProduct._id}/variants/${variant._id}`
        );
        this.handleSuccess(this.$t('products.variant_deleted'));
        
        // Refresh product data
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/products/${this.variantProduct._id}`);
        this.variantProduct = response.data.data;
        this.fetchProducts();
      } catch (error) {
        this.handleError(error, 'Failed to delete variant');
      }
    },
    
    // Subcategory methods
    showSubcategoryManager() {
      this.resetSubcategoryForm();
      this.$modal.show('subcategory-modal');
    },
    hideSubcategoryManager() {
      this.$modal.hide('subcategory-modal');
      this.resetSubcategoryForm();
    },
    resetSubcategoryForm() {
      this.newSubcategory = {
        name: '',
        displayName: '',
        parentCategory: '',
        sortOrder: 0
      };
      this.editingSubcategory = null;
    },
    editSubcategory(sub) {
      this.editingSubcategory = sub;
      this.newSubcategory = {
        name: sub.name,
        displayName: sub.displayName,
        parentCategory: sub.parentCategory,
        sortOrder: sub.sortOrder || 0
      };
    },
    cancelEditSubcategory() {
      this.resetSubcategoryForm();
    },
    async saveSubcategory() {
      if (!this.newSubcategory.name || !this.newSubcategory.displayName || !this.newSubcategory.parentCategory) {
        this.handleError(null, 'All fields are required');
        return;
      }
      
      try {
        if (this.editingSubcategory) {
          await this.axios.put(
            `${process.env.VUE_APP_SERVER_URL}/subcategories/${this.editingSubcategory._id}`,
            this.newSubcategory
          );
          this.handleSuccess(this.$t('products.subcategory_updated'));
        } else {
          await this.axios.post(
            `${process.env.VUE_APP_SERVER_URL}/subcategories`,
            this.newSubcategory
          );
          this.handleSuccess(this.$t('products.subcategory_created'));
        }
        
        this.fetchSubcategories();
        this.fetchProducts();
        this.resetSubcategoryForm();
      } catch (error) {
        this.handleError(error, 'Failed to save subcategory');
      }
    },
    deleteSubcategory(sub) {
      this.$modal.show('dialog', {
        title: `<span class="text-red-500">${this.$t('products.confirm_delete_subcategory', { name: sub.displayName })}</span>`,
        buttons: [
          {
            title: '<div class="bg-accent-500 text-white text-sm font-sans py-2">'+this.$t('products.cancel')+'</div>',
            handler: () => {
              this.$modal.hide('dialog');
            }
          },
          {
            title: '<div class="bg-red-500 text-white text-sm font-sans py-2">Ta bort</div>',
            handler: async () => {
              try {
                await this.axios.delete(`${process.env.VUE_APP_SERVER_URL}/subcategories/${sub._id}`);
                this.handleSuccess(this.$t('products.subcategory_deleted'));
                this.fetchSubcategories();
                this.fetchProducts();
              } catch (error) {
                this.handleError(error, 'Failed to delete subcategory');
              }
              this.$modal.hide('dialog');
            }
          }
        ]
      });
    },
    
    // Export methods
    showExportModal() {
      // Reset and initialize export options
      this.exportOptions.selectedGroups = [];
      this.exportOptions.selectedSubcategories = [];
      this.exportOptions.pdfTitle = '';
      this.$modal.show('export-modal');
    },
    hideExportModal() {
      this.$modal.hide('export-modal');
    },
    toggleAllGroups() {
      if (this.allGroupsSelected) {
        this.exportOptions.selectedGroups = [];
        this.exportOptions.selectedSubcategories = [];
      } else {
        this.exportOptions.selectedGroups = this.currentGroupOptions.map(o => o.value);
        if (this.exportOptions.groupBy === 'category') {
          // Select all subcategories
          const allSubs = [];
          this.categoryOptions.forEach(cat => {
            const subs = this.subcategoriesByParent[cat.value] || [];
            subs.forEach(s => allSubs.push(s._id));
            allSubs.push(`${cat.value}-none`); // Include "without subcategory"
          });
          this.exportOptions.selectedSubcategories = allSubs;
        }
      }
    },
    getProductCountForGroup(groupValue) {
      const groupField = this.exportOptions.groupBy === 'category' ? 'category' : 'manufacturer';
      return this.products.filter(p => p[groupField] === groupValue).length;
    },
    getProductCountForSubcategory(categoryValue, subcategoryId) {
      return this.products.filter(p => {
        if (p.category !== categoryValue) return false;
        const productSubcatId = p.subcategory ? (typeof p.subcategory === 'object' ? p.subcategory._id : p.subcategory) : null;
        return String(productSubcatId) === String(subcategoryId);
      }).length;
    },
    getProductCountWithoutSubcategory(categoryValue) {
      return this.products.filter(p => p.category === categoryValue && !p.subcategory).length;
    },
    isCategoryFullySelected(categoryValue) {
      if (this.exportOptions.groupBy === 'manufacturer') {
        return this.exportOptions.selectedGroups.includes(categoryValue);
      }
      const subs = this.subcategoriesByParent[categoryValue] || [];
      const expectedSelections = subs.map(s => s._id).concat([`${categoryValue}-none`]);
      return expectedSelections.every(id => this.exportOptions.selectedSubcategories.includes(id));
    },
    isCategoryPartiallySelected(categoryValue) {
      if (this.exportOptions.groupBy === 'manufacturer') return false;
      const subs = this.subcategoriesByParent[categoryValue] || [];
      const expectedSelections = subs.map(s => s._id).concat([`${categoryValue}-none`]);
      const selectedCount = expectedSelections.filter(id => this.exportOptions.selectedSubcategories.includes(id)).length;
      return selectedCount > 0 && selectedCount < expectedSelections.length;
    },
    toggleCategorySelection(categoryValue) {
      if (this.exportOptions.groupBy === 'manufacturer') {
        // Simple toggle for manufacturers
        const idx = this.exportOptions.selectedGroups.indexOf(categoryValue);
        if (idx > -1) {
          this.exportOptions.selectedGroups.splice(idx, 1);
        } else {
          this.exportOptions.selectedGroups.push(categoryValue);
        }
        return;
      }
      
      // For categories, toggle all subcategories
      const subs = this.subcategoriesByParent[categoryValue] || [];
      const subIds = subs.map(s => s._id).concat([`${categoryValue}-none`]);
      const isFullySelected = this.isCategoryFullySelected(categoryValue);
      
      if (isFullySelected) {
        // Deselect all subcategories
        this.exportOptions.selectedSubcategories = this.exportOptions.selectedSubcategories.filter(id => !subIds.includes(id));
        // Remove from selectedGroups
        const idx = this.exportOptions.selectedGroups.indexOf(categoryValue);
        if (idx > -1) this.exportOptions.selectedGroups.splice(idx, 1);
      } else {
        // Select all subcategories
        subIds.forEach(id => {
          if (!this.exportOptions.selectedSubcategories.includes(id)) {
            this.exportOptions.selectedSubcategories.push(id);
          }
        });
        // Add to selectedGroups
        if (!this.exportOptions.selectedGroups.includes(categoryValue)) {
          this.exportOptions.selectedGroups.push(categoryValue);
        }
      }
    },
    onSubcategoryChange(categoryValue) {
      // Update selectedGroups based on subcategory selection
      const subs = this.subcategoriesByParent[categoryValue] || [];
      const subIds = subs.map(s => s._id).concat([`${categoryValue}-none`]);
      const hasAnySelected = subIds.some(id => this.exportOptions.selectedSubcategories.includes(id));
      
      const idx = this.exportOptions.selectedGroups.indexOf(categoryValue);
      if (hasAnySelected && idx === -1) {
        this.exportOptions.selectedGroups.push(categoryValue);
      } else if (!hasAnySelected && idx > -1) {
        this.exportOptions.selectedGroups.splice(idx, 1);
      }
    },
    isProductSelectedForExport(product) {
      if (this.exportOptions.groupBy === 'manufacturer') {
        return this.exportOptions.selectedGroups.includes(product.manufacturer);
      }
      
      // For categories, check subcategory selection
      const productSubcatId = product.subcategory 
        ? (typeof product.subcategory === 'object' ? product.subcategory._id : product.subcategory) 
        : null;
      
      if (productSubcatId) {
        return this.exportOptions.selectedSubcategories.includes(productSubcatId);
      } else {
        // Product has no subcategory
        return this.exportOptions.selectedSubcategories.includes(`${product.category}-none`);
      }
    },
    async generatePDF() {
      if (this.exportProductCount === 0) return;
      
      this.isGeneratingPDF = true;
      
      try {
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 15;
        
        // Colors
        const primaryColor = [0, 51, 102]; // Securdia blue
        const secondaryColor = [100, 100, 100];
        
        // Load logo
        let logoDataUrl = null;
        try {
          const logoImg = await this.loadImage(require('@/assets/logos/logo_horizontal.png'));
          logoDataUrl = logoImg;
        } catch (e) {
          console.warn('Could not load logo:', e);
        }
        
        // Header
        let yPos = margin;
        
        // Add logo if available
        if (logoDataUrl) {
          doc.addImage(logoDataUrl, 'PNG', margin, yPos, 45, 12);
        }
        
        // Title
        const title = this.exportOptions.pdfTitle || this.$t('products.pricelist_default_title');
        doc.setFontSize(20);
        doc.setTextColor(...primaryColor);
        doc.text(title, pageWidth - margin, yPos + 8, { align: 'right' });
        
        // Date
        yPos += 18;
        doc.setFontSize(10);
        doc.setTextColor(...secondaryColor);
        const today = new Date().toLocaleDateString('sv-SE');
        doc.text(`${this.$t('products.generated_date')}: ${today}`, pageWidth - margin, yPos, { align: 'right' });
        
        // Separator line
        yPos += 5;
        doc.setDrawColor(...primaryColor);
        doc.setLineWidth(0.5);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        
        yPos += 10;
        
        // Get products grouped by selection
        const groupField = this.exportOptions.groupBy === 'category' ? 'category' : 'manufacturer';
        const selectedProducts = this.products.filter(p => this.isProductSelectedForExport(p));
        
        // Group products
        const grouped = {};
        selectedProducts.forEach(product => {
          const key = product[groupField];
          if (!grouped[key]) {
            grouped[key] = [];
          }
          grouped[key].push(product);
        });
        
        // Sort products within each group
        Object.keys(grouped).forEach(key => {
          grouped[key].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        });
        
        // Build table headers (use shorter labels for PDF)
        const headers = [this.$t('products.product_name')];
        if (this.exportOptions.fields.articleNumber) headers.push(this.$t('products.article_number'));
        if (this.exportOptions.fields.purchasePrice) headers.push('Inköpspris');
        if (this.exportOptions.fields.recommendedCashPrice) headers.push('Kontantpris');
        if (this.exportOptions.fields.recommendedRentalPrice) headers.push('Hyrespris');
        
        // Process each group
        for (const groupValue of this.exportOptions.selectedGroups) {
          const products = grouped[groupValue] || [];
          if (products.length === 0) continue;
          
          const groupLabel = this.exportOptions.groupBy === 'category' 
            ? this.getCategoryLabel(groupValue) 
            : this.getManufacturerLabel(groupValue);
          
          // Check if we need a new page
          if (yPos > pageHeight - 40) {
            doc.addPage();
            yPos = margin;
          }
          
          // Group header
          doc.setFontSize(14);
          doc.setTextColor(...primaryColor);
          doc.setFont(undefined, 'bold');
          doc.text(groupLabel, margin, yPos);
          yPos += 2;
          
          // Build table data
          const tableData = [];
          
          for (const product of products) {
            const productName = product.display_name 
              ? `${product.display_name} (${product.name})` 
              : product.name;
            
            const row = [productName];
            if (this.exportOptions.fields.articleNumber) row.push(product.articleNumber || '-');
            if (this.exportOptions.fields.purchasePrice) row.push(this.formatPrice(product.purchase_price));
            if (this.exportOptions.fields.recommendedCashPrice) row.push(this.formatPrice(product.recommended_cash_price));
            if (this.exportOptions.fields.recommendedRentalPrice) row.push(this.formatPrice(product.recommended_rental_price));
            
            tableData.push(row);
            
            // Add variants if enabled
            if (this.exportOptions.fields.includeVariants && product.hasVariants && product.variants && product.variants.length > 0) {
              for (const variant of product.variants) {
                const variantRow = [`    - ${variant.name}`];
                if (this.exportOptions.fields.articleNumber) variantRow.push(variant.articleNumber || '-');
                if (this.exportOptions.fields.purchasePrice) variantRow.push(this.formatPrice(variant.purchase_price));
                if (this.exportOptions.fields.recommendedCashPrice) variantRow.push(this.formatPrice(variant.recommended_cash_price));
                if (this.exportOptions.fields.recommendedRentalPrice) variantRow.push(this.formatPrice(variant.recommended_rental_price));
                tableData.push(variantRow);
              }
            }
          }
          
          // Generate table
          doc.autoTable({
            startY: yPos,
            head: [headers],
            body: tableData,
            margin: { left: margin, right: margin },
            headStyles: {
              fillColor: primaryColor,
              textColor: [255, 255, 255],
              fontStyle: 'bold',
              fontSize: 9
            },
            bodyStyles: {
              fontSize: 9,
              textColor: [50, 50, 50]
            },
            alternateRowStyles: {
              fillColor: [245, 247, 250]
            },
            columnStyles: {
              0: { cellWidth: 'auto' }
            },
            didParseCell: (data) => {
              // Style variant rows
              if (data.section === 'body' && data.row.raw[0] && data.row.raw[0].startsWith('    - ')) {
                data.cell.styles.textColor = [100, 100, 100];
                data.cell.styles.fontStyle = 'italic';
              }
            }
          });
          
          yPos = doc.lastAutoTable.finalY + 10;
        }
        
        // Footer on each page
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(...secondaryColor);
          doc.text(
            `${this.$t('products.page')} ${i} / ${pageCount}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
          );
          doc.text(
            'Securdia AB | www.securdia.se',
            margin,
            pageHeight - 10
          );
        }
        
        // Save the PDF
        const filename = `${title.replace(/[^a-zA-Z0-9åäöÅÄÖ\s]/g, '').replace(/\s+/g, '_')}_${today}.pdf`;
        doc.save(filename);
        
        this.handleSuccess(this.$t('products.pdf_generated'));
        this.hideExportModal();
        
      } catch (error) {
        console.error('PDF generation error:', error);
        this.handleError(error, this.$t('products.pdf_error'));
      } finally {
        this.isGeneratingPDF = false;
      }
    },
    loadImage(src) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;
        img.src = src;
      });
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === 0) return '-';
      return `${value.toLocaleString('sv-SE')} kr`;
    }
  },
  created() {
    this.setPageTitle(this.$t('products.title'), 'products');
  }
};
</script>

<style scoped>
/* Simple transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 0.2s;
}
.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}
</style>
